import { CheckCircle, type IconProps, Info, Warning, X, XCircle } from '@/shared/icons/phosphor'
import { type Component, type JSX, Show } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { IconButton } from '../IconButton/IconButton'

export type AlertVariant = 'error' | 'warning' | 'info' | 'success'

interface AlertBaseProps {
  variant?: AlertVariant
  title?: string | undefined
  icon?: boolean
  announce?: boolean
  size?: 'sm' | 'md'
  action?: JSX.Element
  children: JSX.Element
  class?: string
}

type AlertDismissProps =
  | { onDismiss: () => void; dismissLabel: string }
  | { onDismiss?: undefined; dismissLabel?: undefined }

export type AlertProps = AlertBaseProps & AlertDismissProps

const variantClasses: Record<AlertVariant, string> = {
  error: 'bg-[var(--color-error-50)] border-[var(--color-error-200)]',
  warning: 'bg-[var(--color-warning-50)] border-[var(--color-warning-200)]',
  info: 'bg-[var(--color-info-50)] border-[var(--color-info-200)]',
  success: 'bg-[var(--color-success-50)] border-[var(--color-success-200)]',
}

export const alertToneText: Record<AlertVariant, string> = {
  error: 'text-[color:var(--color-error-700)] dark:text-[color:var(--color-error-600)]',
  warning: 'text-[color:var(--color-warning-800)] dark:text-[color:var(--color-warning-600)]',
  info: 'text-[color:var(--color-info-700)] dark:text-[color:var(--color-info-600)]',
  success: 'text-[color:var(--color-success-700)] dark:text-[color:var(--color-success-600)]',
}

export const alertIcons: Record<AlertVariant, Component<IconProps>> = {
  error: XCircle,
  warning: Warning,
  info: Info,
  success: CheckCircle,
}

const CALLER_PADDING = /(^|\s)([a-z]+:)*-?p[xytrbl]?-/

export function Alert(props: AlertProps) {
  const variant = () => props.variant ?? 'info'
  const decorated = () =>
    props.icon === true ||
    props.title !== undefined ||
    props.onDismiss !== undefined ||
    props.action !== undefined
  const padding = () => {
    if (CALLER_PADDING.test(props.class ?? '')) {
      return ''
    }
    return props.size === 'sm' ? 'p-3' : 'p-3 sm:p-4'
  }

  return (
    <div
      role={props.announce === false ? 'note' : 'alert'}
      class={`
        ${padding()}
        ${props.size === 'sm' ? 'text-[length:var(--text-xs)] leading-[var(--leading-snug)]' : 'text-[length:var(--text-sm)]'}
        rounded-[var(--radius-16)] border
        [&_a]:underline [&_a]:underline-offset-2
        text-[color:var(--color-text)]
        ${variantClasses[variant()]}
        ${props.class ?? ''}
      `.trim()}
    >
      <Show when={decorated()} fallback={props.children}>
        <div class="flex flex-wrap items-start gap-x-2.5 gap-y-2">
          <Show when={props.icon}>
            <span data-alert-icon class="flex h-[1lh] shrink-0 items-center">
              <Dynamic
                component={alertIcons[variant()]}
                class={`${props.size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${alertToneText[variant()]}`}
                aria-hidden="true"
              />
            </span>
          </Show>
          <div class="min-w-0 flex-1 sm:text-pretty">
            <Show when={props.title}>
              <p
                class={`mb-1 sm:text-pretty font-[var(--font-semibold)] ${alertToneText[variant()]}`}
              >
                {props.title}
              </p>
            </Show>
            {props.children}
          </div>
          <Show when={props.action}>
            <div
              data-alert-action
              class="order-last basis-full sm:order-none sm:basis-auto shrink-0"
            >
              {props.action}
            </div>
          </Show>
          <Show when={props.onDismiss}>
            {(dismiss) => (
              <IconButton
                label={props.dismissLabel ?? ''}
                size="sm"
                tone="inherit"
                class="-my-2 -mr-2"
                onClick={() => dismiss()()}
              >
                <X class="w-4 h-4" aria-hidden="true" />
              </IconButton>
            )}
          </Show>
        </div>
      </Show>
    </div>
  )
}
